import React from "react";
import { Axios } from "api/axios";
import { toast } from "react-toastify";

interface EditableTextCellProps {
  value?: string;
  canChange?: boolean;
  rowId: string;
  fieldName: string; // Field name for API payload (e.g., "follow_up_remark", "notes")
  placeholder?: string;
  maxLength?: number;
  multiline?: boolean; // Whether to use textarea instead of input
  successMessage?: string;
  errorMessage?: string;
  rows?: number; // For textarea
  onSuccess?: () => void;
  truncateLines?: number; // Number of lines to show before truncating (optional)
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value = "",
  canChange = false,
  rowId,
  fieldName,
  placeholder = "Enter text...",
  maxLength,
  multiline = false,
  successMessage,
  errorMessage,
  rows = 3,
  onSuccess,
  truncateLines,
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [cellValue, setCellValue] = React.useState(value);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setCellValue(value || "");
  }, [value]);

  const handleSave = async () => {
    if (cellValue === (value || "")) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setIsEditing(false);
    try {
      const payload: any = {};
      payload[fieldName] = cellValue || null;

      await Axios.patch(
        `api/quotation/current-status-validity-update/${rowId}/`,
        payload
      );

      toast.success(
        successMessage || `${fieldName.replace(/_/g, " ")} updated successfully!`
      );
      onSuccess?.();
    } catch (err: any) {
      setCellValue(value || "");
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        errorMessage ||
        `Failed to update ${fieldName.replace(/_/g, " ")}`;
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Enter" && multiline && e.ctrlKey) {
      // Ctrl+Enter to save in multiline mode
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      setCellValue(value || "");
      setIsEditing(false);
    }
  };

  const displayValue = cellValue || "-";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "6px",
        width: "100%",
        minWidth: "200px",
        maxWidth: "300px",
      }}
    >
      {!canChange ? (
        <div
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            width: "100%",
            color: "#6c757d",
          }}
        >
          {displayValue}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "6px", width: "100%" }}>
          {isEditing ? (
            multiline ? (
              <textarea
                value={cellValue}
                onChange={(e) => setCellValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={isSaving}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                style={{
                  padding: "4px 8px",
                  borderRadius: 4,
                  fontSize: "0.85rem",
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "text",
                  border: "1px solid #005D97",
                  outline: "none",
                  resize: "vertical",
                  width: "100%",
                  fontFamily: "inherit",
                }}
              />
            ) : (
              <input
                type="text"
                value={cellValue}
                onChange={(e) => setCellValue(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={isSaving}
                placeholder={placeholder}
                maxLength={maxLength}
                style={{
                  padding: "2px 6px",
                  borderRadius: 4,
                  fontSize: "0.85rem",
                  opacity: isSaving ? 0.6 : 1,
                  cursor: isSaving ? "not-allowed" : "text",
                  border: "1px solid #005D97",
                  outline: "none",
                  width: "100%",
                }}
              />
            )
          ) : (
            <div
              onClick={() => !isSaving && canChange && setIsEditing(true)}
              style={{
                cursor: isSaving ? "wait" : canChange ? "pointer" : "default",
                opacity: isSaving ? 0.6 : 1,
                padding: "2px 4px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                whiteSpace: "normal",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                width: "100%",
                color: displayValue === "-" ? "#6c757d" : "inherit",
                minHeight: "20px",
                ...(truncateLines && !isEditing
                  ? {
                      display: "-webkit-box",
                      WebkitLineClamp: truncateLines,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: "1.5",
                      maxHeight: `${truncateLines * 1.5}em`,
                    }
                  : {}),
              }}
              onMouseEnter={(e) => {
                if (canChange && !isSaving) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                if (canChange && !isSaving) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              {isSaving ? "Saving..." : displayValue}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableTextCell;

